import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  IconArrowLeft,
  IconExternalLink,
  IconReceipt,
  IconTicket,
} from '@tabler/icons-react';
import type { CustomerIndexItemDto, Vendor } from '@techgitter/contracts';
import { useCustomerServices } from '../../hooks/useCustomerServices';
import { CustomerServicesPanel } from '../tickets/Customer/CustomerServicesPanel';
import { EsetManagePanel } from '../eset/EsetManagePanel';
import { useCustomerAliases } from '../../hooks/useCustomerAliases';
import { CustomerAliasesPanel } from './CustomerAliasesPanel';
import { CustomerEditForm } from './CustomerEditForm';
import { CustomerTicketTemplateOverridesPanel } from './CustomerTicketTemplateOverridesPanel';
import { CustomerInvoiceSchedulesPanel } from './CustomerInvoiceSchedulesPanel';
import { getVendors } from '../../services/vendorService';
import { useCustomerEditForm } from '../../hooks/useCustomerEditForm';
import { CARD_SX } from '../../styles/designSystem';
import './CustomerDetailView.css';

const REPAIRSHOPR_BASE_URL = 'https://techsolvers.repairshopr.com';

/** Compact secondary/outlined header action button — accent text, hairline
 *  border, soft accent hover (design-system §9). */
const headerBtnSx = {
  fontSize: 13,
  fontWeight: 500,
  py: 0.25,
  px: 1.25,
  minHeight: 0,
  color: '#4A90D9',
  borderColor: '#c8d4e0',
  '&:hover': { borderColor: '#4A90D9', bgcolor: '#EDF5FF' },
  '& .MuiButton-startIcon': { mr: 0.5 },
} as const;

interface CustomerDetailViewProps {
  customer: CustomerIndexItemDto;
  onBack: () => void;
  isAdmin?: boolean;
}

export const CustomerDetailView: React.FC<CustomerDetailViewProps> = ({ customer, onBack, isAdmin = false }) => {
  const {
    services,
    isLoading: servicesLoading,
    error: servicesError,
    canEditServices,
    refresh: refreshServices,
    create: createService,
    update: updateService,
  } = useCustomerServices(customer.dataverseCustomerId, 'all');

  const {
    aliases,
    isLoading: aliasesLoading,
    error: aliasesError,
    refresh: refreshAliases,
    create: createAlias,
    update: updateAlias,
    remove: removeAlias,
  } = useCustomerAliases(customer.dataverseCustomerId);

  const [esetPanelOpen, setEsetPanelOpen] = useState(false);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  useEffect(() => {
    const controller = new AbortController();
    getVendors(controller.signal).then(setVendors).catch(() => {});
    return () => controller.abort();
  }, []);

  const {
    customer: editableCustomer,
    isLoading: editLoading,
    isSaving,
    error: editLoadError,
    saveError,
    lastSaveResult,
    save: saveCustomer,
  } = useCustomerEditForm(customer.dataverseCustomerId, isAdmin);

  const handleSave = async (updates: Parameters<typeof saveCustomer>[0]) => {
    return saveCustomer(updates);
  };

  const openRsCustomer = () => {
    window.open(
      `${REPAIRSHOPR_BASE_URL}/customers/${customer.repairShoprCustomerId}`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  const openRsInvoices = () => {
    window.open(
      `${REPAIRSHOPR_BASE_URL}/invoices?search%5Bcustomer_id%5D=${customer.repairShoprCustomerId}`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  const openRsTickets = () => {
    window.open(
      `${REPAIRSHOPR_BASE_URL}/tickets?commit=Search&ticket_search%5Bquery%5D%5Ball_fields%5D=customer_id%3A${customer.repairShoprCustomerId}&ticket_search_id=all`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  const infoFields = [
    { label: 'Type', value: customer.customerType },
    { label: 'Designation', value: customer.designation },
    { label: 'Phone', value: customer.phone },
    { label: 'Mobile', value: customer.mobile },
    { label: 'Email', value: customer.email },
    { label: 'Domains', value: customer.domains },
    { label: 'Alias', value: customer.alias },
  ].filter((f) => f.value);

  return (
    <div className="customer-detail">
      <div className="customer-detail__inner">
        <button type="button" className="customer-detail__back" onClick={onBack}>
          <IconArrowLeft size={16} stroke={1.5} />
          Back to customers
        </button>

        {/* Identity card — name + type/designation + RepairShopr links */}
        <Box sx={{ ...CARD_SX, p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 20, fontWeight: 600, color: '#1a1a1a', lineHeight: 1.2 }}>
                {customer.displayName}
              </Typography>
              <Typography sx={{ fontSize: 12, color: '#888' }}>
                {[customer.customerType, customer.designation].filter(Boolean).join(' · ')}
              </Typography>
            </Box>
            <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                size="small"
                sx={headerBtnSx}
                startIcon={<IconExternalLink size={16} stroke={1.5} />}
                onClick={openRsCustomer}
              >
                RepairShopr
              </Button>
              <Button
                variant="outlined"
                size="small"
                sx={headerBtnSx}
                startIcon={<IconReceipt size={16} stroke={1.5} />}
                onClick={openRsInvoices}
              >
                Invoices
              </Button>
              <Button
                variant="outlined"
                size="small"
                sx={headerBtnSx}
                startIcon={<IconTicket size={16} stroke={1.5} />}
                onClick={openRsTickets}
              >
                Tickets
              </Button>
            </Box>
          </Box>
        </Box>

        {/* Details card — edit form (admin) or read-only header facts */}
        <Box sx={{ ...CARD_SX, p: 2 }}>
          {isAdmin ? (
            editLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : editLoadError ? (
              <Alert severity="error">{editLoadError}</Alert>
            ) : editableCustomer ? (
              <CustomerEditForm
                customer={editableCustomer}
                isSaving={isSaving}
                saveError={saveError}
                lastSaveResult={lastSaveResult}
                onSave={handleSave}
              />
            ) : null
          ) : (
            <>
              <div className="customer-detail__card-title">Customer details</div>
              {infoFields.length > 0 ? (
                <div className="customer-detail__facts">
                  {infoFields.map((field) => (
                    <div key={field.label}>
                      <div className="customer-detail__fact-label">{field.label}</div>
                      <div className="customer-detail__fact-value">{field.value}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <Typography sx={{ fontSize: 13, color: '#888' }}>
                  No additional details on file.
                </Typography>
              )}
            </>
          )}
        </Box>

        {/* Relationships card — Services + Aliases stack as flush sections (§9) */}
        <Box sx={{ ...CARD_SX, overflow: 'hidden' }}>
          <CustomerServicesPanel
            dataverseCustomerId={customer.dataverseCustomerId}
            services={services}
            isLoading={servicesLoading}
            error={servicesError}
            canEditServices={canEditServices}
            refresh={refreshServices}
            create={createService}
            update={updateService}
            density="comfortable"
            onManageEset={() => setEsetPanelOpen(true)}
            customerName={customer.displayName}
          />
          <CustomerAliasesPanel
            aliases={aliases}
            vendors={vendors}
            isLoading={aliasesLoading}
            error={aliasesError}
            canEditMatchingAliases={true}
            canDeleteAliases={true}
            canToggleActive={true}
            refresh={refreshAliases}
            create={createAlias}
            update={updateAlias}
            remove={removeAlias}
            density="comfortable"
          />
        </Box>

        {/* Scheduled Invoices — read-only for all users; admins get the manage link */}
        <CustomerInvoiceSchedulesPanel
          dataverseCustomerId={customer.dataverseCustomerId}
          isAdmin={isAdmin}
        />

        {isAdmin && (
          <CustomerTicketTemplateOverridesPanel
            customerId={customer.dataverseCustomerId}
            customerLabel={customer.displayName}
          />
        )}

        <EsetManagePanel
          open={esetPanelOpen}
          onClose={() => setEsetPanelOpen(false)}
          customerGuid={customer.dataverseCustomerId}
          customerId={customer.repairShoprCustomerId}
          customerName={customer.displayName}
        />
      </div>
    </div>
  );
};
